import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

// ─────────────────────────────────────────────────────────────
//  Panel admin (Fase 3): alojamientos + calendario de ocupación de estancias.
//  Define casas/habitaciones (nombre + capacidad), asigna estancias a un
//  alojamiento con sus fechas, y ve la ocupación por mes (con aviso de solapes).
// ─────────────────────────────────────────────────────────────

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const pad = n => String(n).padStart(2, '0');
const inputStyle = {
  padding: '.35rem .5rem', borderRadius: 6, border: '1px solid rgba(255,255,255,.18)',
  background: 'rgba(0,0,0,.25)', color: 'var(--white)', fontSize: '.85rem',
};

export default function AdminAlojamientos() {
  const { token } = useAuth();
  const [alojamientos, setAlojamientos] = useState([]);
  const [estancias, setEstancias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nombre, setNombre] = useState('');
  const [capacidad, setCapacidad] = useState('');
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [a, e] = await Promise.all([api.getAlojamientos(token), api.getEstancias(token)]);
      setAlojamientos(a.alojamientos || []);
      setEstancias(e.estancias || []);
    } catch (err) {
      setError(err.message || 'No se pudo cargar.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const addAlojamiento = async () => {
    if (!nombre.trim()) { alert('Pon un nombre.'); return; }
    try {
      await api.createAlojamiento(token, { nombre: nombre.trim(), capacidad: Number(capacidad) || 0 });
      setNombre(''); setCapacidad('');
      await load();
    } catch (e) { alert(e.message || 'No se pudo crear.'); }
  };

  const delAlojamiento = async (a) => {
    if (!window.confirm(`¿Eliminar el alojamiento "${a.nombre}"?`)) return;
    try { await api.deleteAlojamiento(token, a.id); await load(); }
    catch (e) { alert(e.message || 'No se pudo eliminar.'); }
  };

  const asignar = async (est, body) => {
    try { await api.updateEstanciaAlojamiento(token, est.id, body); await load(); }
    catch (e) { alert(e.message || 'No se pudo asignar.'); }
  };

  const cambiarMes = (delta) => setYm(({ y, m }) => {
    const d = new Date(y, m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  // ── Ocupación del mes ──
  const diasMes = new Date(ym.y, ym.m + 1, 0).getDate();
  const dias = Array.from({ length: diasMes }, (_, i) => i + 1);
  const dayStr = d => `${ym.y}-${pad(ym.m + 1)}-${pad(d)}`;
  const ocupantes = (aloId, ds) => estancias.filter(e =>
    e.alojamientoId === aloId && e.checkInDate && e.checkOutDate && e.checkInDate <= ds && ds < e.checkOutDate);
  const sinAsignar = estancias.filter(e => !e.alojamientoId);

  const cellStyle = (list) => {
    if (list.length === 0) return { background: 'transparent' };
    if (list.length > 1) return { background: '#b5482f', color: '#fff' }; // solape
    return { background: 'rgba(90,122,80,.55)', color: '#fff' };
  };

  return (
    <div className="main-content page-enter">
      <div style={{ marginBottom: '1rem' }}>
        <div className="section-label">Alojamientos y calendario</div>
        <h1 style={{ fontSize: '1.4rem', color: 'var(--white)', margin: 0 }}>Alojamientos y ocupación (estancias)</h1>
        <p style={{ color: 'var(--mid)', fontSize: '.85rem', marginTop: '.3rem' }}>
          Define casas/habitaciones, asigna estancias con sus fechas y consulta la ocupación por mes. En rojo = solape.
        </p>
      </div>

      {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}

      {/* Gestión de alojamientos */}
      <div className="card" style={{ padding: '1rem', marginBottom: '1.2rem' }}>
        <div style={{ color: 'var(--white)', fontWeight: 600, marginBottom: '.6rem' }}>🛏️ Alojamientos</div>
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '.8rem' }}>
          <input style={{ ...inputStyle, flex: 1, minWidth: 160 }} placeholder="Nombre (p. ej. Casa del Roble)" value={nombre} onChange={e => setNombre(e.target.value)} />
          <input style={{ ...inputStyle, width: 110 }} type="number" min="0" placeholder="Capacidad" value={capacidad} onChange={e => setCapacidad(e.target.value)} />
          <button className="btn btn-primary btn-sm" onClick={addAlojamiento}>➕ Añadir</button>
        </div>
        {alojamientos.length === 0 ? (
          <div style={{ color: 'var(--mid)', fontSize: '.85rem' }}>Aún no hay alojamientos.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
            {alojamientos.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, padding: '.35rem .6rem' }}>
                <span style={{ color: 'var(--white)', fontWeight: 600, fontSize: '.85rem' }}>{a.nombre}</span>
                <span style={{ color: 'var(--mid)', fontSize: '.78rem' }}>👤 {a.capacidad || '—'}</span>
                <button onClick={() => delAlojamiento(a)} title="Eliminar" style={{ background: 'none', border: 'none', color: '#e2796a', cursor: 'pointer', fontSize: '.85rem' }}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Calendario de ocupación */}
      <div className="card" style={{ padding: '1rem', marginBottom: '1.2rem', overflowX: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.7rem' }}>
          <div style={{ color: 'var(--white)', fontWeight: 600 }}>🗓️ Ocupación — {MESES[ym.m]} {ym.y}</div>
          <div style={{ display: 'flex', gap: '.4rem' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => cambiarMes(-1)}>‹ Mes ant.</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setYm({ y: now.getFullYear(), m: now.getMonth() })}>Hoy</button>
            <button className="btn btn-ghost btn-sm" onClick={() => cambiarMes(1)}>Mes sig. ›</button>
          </div>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '1.5rem' }}><span className="spinner spinner-lg" /></div>
        ) : (
          <table style={{ borderCollapse: 'collapse', fontSize: '.7rem' }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, background: 'var(--card-bg, #1a1612)', textAlign: 'left', padding: '.2rem .4rem', color: 'var(--mid)', minWidth: 140 }}>Alojamiento</th>
                {dias.map(d => (
                  <th key={d} style={{ width: 20, color: 'var(--mid)', fontWeight: 500, padding: 0 }}>{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {alojamientos.map(a => (
                <tr key={a.id}>
                  <td style={{ position: 'sticky', left: 0, background: 'var(--card-bg, #1a1612)', color: 'var(--white)', padding: '.2rem .4rem', whiteSpace: 'nowrap' }}>{a.nombre}</td>
                  {dias.map(d => {
                    const list = ocupantes(a.id, dayStr(d));
                    return (
                      <td key={d} title={list.map(e => e.familyName || e.contactEmail).join(', ')}
                        style={{ width: 20, height: 22, border: '1px solid rgba(255,255,255,.06)', textAlign: 'center', ...cellStyle(list) }}>
                        {list.length > 1 ? '!' : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {alojamientos.length === 0 && (
                <tr><td colSpan={diasMes + 1} style={{ color: 'var(--mid)', padding: '.5rem' }}>Crea alojamientos para ver la ocupación.</td></tr>
              )}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: '.5rem', fontSize: '.72rem', color: 'var(--mid)', display: 'flex', gap: '1rem' }}>
          <span><span style={{ display: 'inline-block', width: 12, height: 12, background: 'rgba(90,122,80,.55)', borderRadius: 2, verticalAlign: 'middle' }} /> Ocupado</span>
          <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#b5482f', borderRadius: 2, verticalAlign: 'middle' }} /> Solape (conflicto)</span>
        </div>
      </div>

      {/* Asignación de estancias */}
      <div className="card" style={{ padding: '1rem' }}>
        <div style={{ color: 'var(--white)', fontWeight: 600, marginBottom: '.6rem' }}>
          🏨 Asignar estancias {sinAsignar.length > 0 && <span style={{ color: 'var(--warn)', fontSize: '.8rem' }}>· {sinAsignar.length} sin alojamiento</span>}
        </div>
        {estancias.length === 0 ? (
          <div style={{ color: 'var(--mid)', fontSize: '.85rem' }}>No hay estancias todavía.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            {estancias.map(e => (
              <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: '.6rem', flexWrap: 'wrap', padding: '.5rem .6rem', borderRadius: 8, background: 'rgba(255,255,255,.02)', border: `1px solid ${e.alojamientoId ? 'rgba(255,255,255,.08)' : 'rgba(232,164,48,.3)'}` }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ color: 'var(--white)', fontSize: '.85rem', fontWeight: 600 }}>{e.familyName || e.contactEmail}</div>
                  <div style={{ color: 'var(--mid)', fontSize: '.75rem' }}>{e.concepto || ''}</div>
                </div>
                <input style={{ ...inputStyle }} type="date" value={e.checkInDate || ''} onChange={ev => asignar(e, { checkInDate: ev.target.value })} title="Entrada" />
                <input style={{ ...inputStyle }} type="date" value={e.checkOutDate || ''} onChange={ev => asignar(e, { checkOutDate: ev.target.value })} title="Salida" />
                <select style={{ ...inputStyle, minWidth: 150 }} value={e.alojamientoId || ''} onChange={ev => asignar(e, { alojamientoId: ev.target.value || null })}>
                  <option value="">— Sin alojamiento —</option>
                  {alojamientos.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
